import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { EVENTS } from "@innovid/core-agent-ui";
import { useFlag } from "@innovid/feature-flags-react";
import { FEATURE_FLAGS } from "featureFlag/featureFlag";
import { API_URL } from "utils/constants";
import { getDecisioningContext, handleDecisioningResponse } from "./decisioningAgent";

import "@innovid/core-agent-ui/assets/core-agent-ui.css";

const CORE_AGENT_APP_ID = "CREATIVE_MANAGER";
const HEADER_HEIGHT = 65; //65px

//dtree agent is called "creatives-strategy-agent".
//note: currently core-agent-ui only allows a single apiUrl per ui instance
//that will need to be changed to support multiple agents served from different urls (versioning agent, qa agent, etc)
const CORE_AGENT_API_URL = `${API_URL}/strategyagent`;

const AGENT_CONFIG = {
	DECISIONING: {
		// hopi only has enough context to create new version trees, not have enough context to edit existing trees.
		buttonPrompts:["Create a new decision tree based on available versions"],
		getContext:getDecisioningContext,
		handleResponse:handleDecisioningResponse,
	},
};

const DEFAULT_AGENT = "DECISIONING";

function CoreAgentBlade(){
	const isNivoChatEnabled = useFlag(FEATURE_FLAGS.HOPI_NIVO_CHAT);
	const dispatch = useDispatch();
	const agentRef = useRef(null);
	const notifiedTreeIds = useRef(new Set());
	const { pathname } = useLocation();
	const defaultAgentConfig = AGENT_CONFIG[DEFAULT_AGENT];
	const context = useMemo(() => defaultAgentConfig.getContext(pathname), [defaultAgentConfig, pathname]);

	useEffect(() => {
		if(agentRef.current){
			agentRef.current.context = context;
			agentRef.current.overrideButtonPrompts = defaultAgentConfig.buttonPrompts;
		}
	}, [context, defaultAgentConfig, isNivoChatEnabled]);

	useEffect(() => {
		const agent = agentRef.current;
		if(!agent){ return; }

		const handleResponse = (event) => {
			const agentConfig = AGENT_CONFIG[event?.detail?.agent];
			agentConfig?.handleResponse(event, { context, notifiedTreeIds:notifiedTreeIds.current, dispatch });
		};

		agent.addEventListener(EVENTS.RESPONSE, handleResponse);
		return () => agent.removeEventListener(EVENTS.RESPONSE, handleResponse);
	}, [context, dispatch, isNivoChatEnabled]);

	if(!isNivoChatEnabled){ return null; }

	// <core-agent /> is a custom web component built in svelte, from @innovid/core-agent-ui
	// it gets registered globally when @innovid/core-agent-ui is imported
	return (
		<core-agent
			ref={agentRef}
			apiUrl={CORE_AGENT_API_URL}
			app={CORE_AGENT_APP_ID}
			height={`calc(100% - ${HEADER_HEIGHT}px)`}
		/>
	);
}

CoreAgentBlade.displayName = "CoreAgentBlade";

export default CoreAgentBlade;
